import { ChangeDetectorRef, Component, ElementRef, Input, NgZone, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { SHARED_MODULES } from 'src/app/modules/shared-imports';
import { PermissionService } from 'src/app/services/permission.service';
import { KeyboardShortcutsService } from 'src/app/commons/keyboard-shortcuts/keyboard-shortcuts.service';
import { QuickSearchComponent } from 'src/app/commons/quick-search/quick-search.component';
import { GlobalSearchComponent } from 'src/app/commons/global-search/global-search.component';
import { SimpleCalculatorComponent } from 'src/app/commons/components/simple-calculator/simple-calculator.component';

export type SearchMode = 'auto' | 'records' | 'stock' | 'calculator';

/** Same intent-detection Quick Search's own parser already recognises as "this is about a
 *  physical dimension" — a field keyword, a comparator with a number, or a bare NxN/NxNxN
 *  shorthand. Deliberately loose/cheap here (this only decides ROUTING, not parsing — Quick
 *  Search's real parser still does the actual interpretation once routed to). */
function looksLikeDimensionQuery(raw: string): boolean {
  // Same "a lone x/X in a slot position means ?" rewrite the parser itself does first, so
  // "4x6xX" routes to Stock exactly like "4x6x?" does. Kept identical to the parser's own rule
  // on purpose — if the two drift, a query parses fine but lands in the wrong tab.
  const text = raw.replace(/([x×*]|by)(\s*)x(?![\w?])/gi, '$1$2?');
  const t = text.toLowerCase();
  if (/\b(girth|width|thick(ness)?|length)\b/.test(t)) return true;
  if (/\d/.test(t) && /\b(over|under|between|min|max|at least|at most)\b/.test(t)) return true;
  // 2026-09-15: a size anywhere in the text, not only when it is the whole text — "4x3x6 costa"
  // used to go to Records and find nothing (found live).
  if (/(^|\s)\d+(\.\d+)?\s*(mm|cm|in|inch|ft|″|')?\s*x\s*\d+(\.\d+)?\s*(mm|cm|in|inch|ft|″|')?(\s*x\s*\d+(\.\d+)?\s*(mm|cm|in|inch|ft|″|')?)?(?=\s|$)/i.test(text.trim())) return true;
  // 2026-09-24: wildcard shorthand ("4x3x?" = any length). Kept as its own check rather than
  // folded into the rule above, so that one's behaviour stays exactly as it is — it requires a
  // digit on both sides of every "x", so a wildcard slot never matched it and "4x3x?" quietly
  // went to Records. At least one real digit is required, so a pure "?x?x?" isn't routed here.
  if (text.includes('?') && /\d/.test(text)
      && /(^|\s)(\d+(\.\d+)?|\?)\s*(mm|cm|in|inch|ft|″|')?\s*x\s*(\d+(\.\d+)?|\?)\s*(mm|cm|in|inch|ft|″|')?(\s*x\s*(\d+(\.\d+)?|\?)\s*(mm|cm|in|inch|ft|″|')?)?(?=\s|$)/i.test(text.trim())) return true;
  return false;
}

/** True only when the whole (whitespace-stripped) string is digits/operators/parens AND
 *  contains at least one operator — mirrors GlobalSearchComponent's own identical helper
 *  (duplicated on purpose rather than exported/shared, so that component's internals stay
 *  completely untouched by this merge — see the unified-search build plan). */
function isMathExpression(text: string): boolean {
  const t = text.replace(/\s+/g, '');
  if (!t || !/^[0-9+\-*/×÷().]+$/.test(t)) return false;
  return /[+\-*/×÷]/.test(t);
}

function evaluateMathExpression(input: string): number | null {
  const expr = input.replace(/×/g, '*').replace(/÷/g, '/').replace(/\s+/g, '');
  if (!expr) return null;
  let pos = 0;
  const peek = () => expr[pos];
  const consume = () => expr[pos++];
  function parseExpression(): number {
    let value = parseTerm();
    while (peek() === '+' || peek() === '-') { const op = consume(); const rhs = parseTerm(); value = op === '+' ? value + rhs : value - rhs; }
    return value;
  }
  function parseTerm(): number {
    let value = parseFactor();
    while (peek() === '*' || peek() === '/') { const op = consume(); const rhs = parseFactor(); value = op === '*' ? value * rhs : value / rhs; }
    return value;
  }
  function parseFactor(): number {
    if (peek() === '+') { consume(); return parseFactor(); }
    if (peek() === '-') { consume(); return -parseFactor(); }
    if (peek() === '(') { consume(); const value = parseExpression(); if (peek() === ')') consume(); return value; }
    const start = pos;
    while (pos < expr.length && /[0-9.]/.test(expr[pos])) pos++;
    if (pos === start) throw new Error('unexpected token');
    return parseFloat(expr.slice(start, pos));
  }
  try {
    const result = parseExpression();
    if (pos !== expr.length) return null;
    return isFinite(result) ? Math.round(result * 1e9) / 1e9 : null;
  } catch { return null; }
}

/** The merged Quick Search — one dialog, four modes (Auto/Records/Stock/Calculator), replacing
 *  the two previously-separate dialogs (GlobalSearchComponent, QuickSearchComponent). Both of
 *  those keep their full internal logic completely untouched (parsing, AI, permission checks,
 *  keyboard nav) — this shell only owns the shared input box, the mode selector, and routing
 *  typed text to whichever one is currently active via their own public setQuery(). See each
 *  component's own `embedded` field comment for how they suppress their own chrome when hosted
 *  here. "Quick Search" is the surviving brand name (shorter, already taught via the `/`
 *  shortcut) — Global Search's capability lives on as the "Records" mode. */
@Component({
  selector: 'app-unified-search',
  standalone: true,
  imports: [SHARED_MODULES, QuickSearchComponent, GlobalSearchComponent, SimpleCalculatorComponent],
  templateUrl: './unified-search.component.html',
  styleUrls: ['./unified-search.component.css'],
})
export class UnifiedSearchComponent implements OnInit, OnDestroy {
  @ViewChild(QuickSearchComponent) quickSearchRef?: QuickSearchComponent;
  @ViewChild(GlobalSearchComponent) globalSearchRef?: GlobalSearchComponent;
  @ViewChild('usInput') usInputRef?: ElementRef<HTMLInputElement>;

  visible = false;
  mode: SearchMode = 'auto';
  queryText = '';

  /** Pure-arithmetic detection while in Auto mode — shown as an inline "= result" row alongside
   *  whichever search results are also showing, never a hard mode-switch away from the input
   *  (that would hijack typing mid-expression). Tapping it / pressing Enter on it is what
   *  actually switches into Calculator mode, prefilled with this answer. */
  autoMathResult: number | null = null;
  calculatorPrefill: number | null = null;

  /** Bound once so removeEventListener in ngOnDestroy actually matches this exact reference. */
  private readonly boundGlobalKeydown = (event: KeyboardEvent) => this.onGlobalKeydown(event);

  // ── Stuck-search fix (2026-09-15) ─────────────────────────────────────────────────────────
  // 1) Escape: p-dialog only closes on Escape when it is the TOP layer (ZIndexUtils.getCurrent()).
  //    Any toast/tooltip/overlay shown after the dialog opened silently swallowed Escape, and our
  //    own Escape handling lived only on the input — so with focus elsewhere nothing closed it.
  //    Now a capture-phase listener closes it whenever it is open (Calculator mode keeps its own
  //    Escape: clear, then back to Auto).
  // 2) Focus: p-dialog focuses the input once, after its open animation. On tally screens the grid
  //    hands focus back to its cell when the in-app keypad closes (AG-Grid stopEditing refocuses
  //    the cell), so typing went nowhere. While open, focus that lands outside the dialog (and not
  //    in another popup) is sent straight back to the search box.
  private readonly boundEscapeCapture = (event: KeyboardEvent) => this.onEscapeCapture(event);
  private readonly boundFocusIn = (event: FocusEvent) => this.onFocusInWhileOpen(event);

  constructor(
    public permissionService: PermissionService,
    private keyboardShortcutsService: KeyboardShortcutsService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {}

  /** Manually attached rather than `@HostListener('document:keydown', ...)` — confirmed live
   *  2026-08-20 that the declarative form silently stopped firing for "/" (and, separately, for
   *  "?" in KeyboardShortcutsComponent — same symptom, different component) in the deployed
   *  build: real keypresses reached the browser and the DOM correctly, but Angular's own handler
   *  never ran, while the click binding on the same component's trigger button worked every
   *  time. Root cause in Angular's global-target HostListener wiring wasn't chased down further;
   *  this sidesteps it entirely by attaching + tearing down the listener ourselves, the same way
   *  the (working) click path is wired, with an explicit zone re-entry + change-detection kick so
   *  a rendering glitch here can't silently reintroduce the same failure mode. */
  ngOnInit(): void {
    document.addEventListener('keydown', this.boundGlobalKeydown);
    document.addEventListener('keydown', this.boundEscapeCapture, true);
    document.addEventListener('focusin', this.boundFocusIn, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.boundGlobalKeydown);
    document.removeEventListener('keydown', this.boundEscapeCapture, true);
    document.removeEventListener('focusin', this.boundFocusIn, true);
  }

  private onEscapeCapture(event: KeyboardEvent): void {
    if (!this.visible || event.key !== 'Escape') return;
    if (this.mode === 'calculator') return; // SimpleCalculatorComponent owns Escape there
    event.preventDefault();
    event.stopPropagation();
    this.ngZone.run(() => {
      this.close();
      this.cdr.detectChanges();
    });
  }

  private onFocusInWhileOpen(event: FocusEvent): void {
    if (!this.visible || this.mode === 'calculator') return;
    const target = event.target as HTMLElement | null;
    if (!target || typeof target.closest !== 'function') return;
    // Inside the search dialog, or inside some other popup (a dialog/menu/overlay/toast opened on
    // top of it) — leave it alone.
    if (target.closest('.us-dialog, .p-dialog, .p-overlay, .p-popover, .p-menu, .p-toast, .p-drawer')) return;
    this.focusInput();
  }

  /** Puts the cursor in the search box (no-op if it is already there or not rendered yet). */
  focusInput(): void {
    const input = this.usInputRef?.nativeElement;
    if (!input || document.activeElement === input) return;
    input.focus({ preventScroll: true });
  }

  /** p-dialog (onShow) — it focuses the input once after the open animation; these extra tries
   *  cover anything that grabs focus right after that (grid cell refocus, keypad close). */
  onDialogShown(): void {
    [0, 150, 400].forEach(ms => setTimeout(() => { if (this.visible) this.focusInput(); }, ms));
  }

  private onGlobalKeydown(event: KeyboardEvent): void {
    this.ngZone.run(() => {
      this.handleSlash(event);
      this.handleCalculatorShortcut(event);
      this.cdr.detectChanges();
    });
  }

  get canRecords(): boolean { return this.permissionService.hasEffective('GLOBAL_SEARCH_ACCESS'); }
  get canStock(): boolean { return this.permissionService.hasEffective('QUICK_SEARCH_ACCESS'); }
  get canUse(): boolean { return this.canRecords || this.canStock; }

  /** What Auto mode actually does right now — never resolves to a mode the user lacks
   *  permission for, even if the text looks like a match for it. */
  get effectiveMode(): 'records' | 'stock' | 'calculator' {
    if (this.mode !== 'auto') return this.mode as 'records' | 'stock' | 'calculator';
    if (this.canStock && looksLikeDimensionQuery(this.queryText)) return 'stock';
    if (this.canRecords) return 'records';
    return 'stock';
  }

  /** Only the modes actually worth showing as buttons — Calculator is always offered once the
   *  trigger itself is visible (no separate permission of its own, matching precedent). */
  get availableModes(): Array<{ value: SearchMode; icon: string; labelKey: string }> {
    const modes: Array<{ value: SearchMode; icon: string; labelKey: string }> = [
      { value: 'auto', icon: 'pi pi-sparkles', labelKey: 'unifiedSearch.mode.auto' },
    ];
    if (this.canRecords) modes.push({ value: 'records', icon: 'pi pi-search', labelKey: 'unifiedSearch.mode.records' });
    if (this.canStock) modes.push({ value: 'stock', icon: 'pi pi-box', labelKey: 'unifiedSearch.mode.stock' });
    modes.push({ value: 'calculator', icon: 'pi pi-calculator', labelKey: 'unifiedSearch.mode.calculator' });
    return modes;
  }

  /** trackBy for the mode-selector *ngFor — availableModes is a GETTER, so it returns a brand
   *  new array of brand new objects on every change-detection cycle. Without this, *ngFor's
   *  default identity-based diffing sees every item as "changed" and destroys + recreates all
   *  4 buttons on every single CD pass (typing in the box, the onDialogShown() focus retries at
   *  0/150/400ms, etc.) — a click landing in the instant one of those swaps the DOM node out
   *  from under the cursor is silently dropped. Real incident: clicking Calculator right after
   *  opening the dialog did nothing (2026-09-23) — same root cause already fixed once elsewhere
   *  in this codebase (getter-built array + *ngFor, see the "*ngFor Getter Microtask Freeze"
   *  fix, v5.47.0). Keying on m.value (stable across every call) makes Angular reuse the same
   *  DOM node instead of replacing it. */
  trackByModeValue(_index: number, m: { value: SearchMode }): SearchMode {
    return m.value;
  }

  get autoResolvedLabelKey(): string | null {
    if (this.mode !== 'auto' || !this.queryText.trim()) return null;
    return this.effectiveMode === 'stock' ? 'unifiedSearch.mode.stock' : 'unifiedSearch.mode.records';
  }

  /** "/" — the ONLY way to open the merged dialog now (Ctrl+K removed 2026-08-20: it was already
   *  flagged earlier this session as conflicting with a real browser shortcut, and keeping two
   *  openers around after that was just unresolved debt, not a real feature). preventDefault()
   *  fires as early as the input-focus exemption allows — Firefox's own built-in "Quick Find"
   *  feature ALSO activates on a bare "/" outside a text field, and can win the race against a
   *  page's own handler if preventDefault() isn't called immediately once "/" is confirmed safe
   *  to intercept; the permission/already-open checks are deliberately AFTER preventDefault so
   *  they can't accidentally delay it. */
  handleSlash(event: KeyboardEvent): void {
    if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
    event.preventDefault();
    event.stopPropagation();
    if (this.visible || !this.canUse) return;
    this.open();
  }

  /** Ctrl+Alt+C — jumps straight into Calculator mode, skipping Auto, for someone who only ever
   *  wants the calculator. Same shortcut Global Search's own standalone version already used. */
  handleCalculatorShortcut(event: KeyboardEvent): void {
    // event.key can be undefined for some synthetic/extension-dispatched KeyboardEvents —
    // confirmed via a real prod error (TypeError: Cannot read properties of undefined
    // (reading 'toLowerCase')) before this guard existed.
    if ((event.key || '').toLowerCase() !== 'c' || !event.ctrlKey || !event.altKey) return;
    if (this.visible || !this.canUse) return;
    const target = event.target as HTMLElement | null;
    const tag = target?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target?.isContentEditable) return;
    event.preventDefault();
    this.open();
    this.setMode('calculator');
  }

  /**
   * 2026-10-07: with the new (glass) header on a wide screen the search opens RIGHT OVER the header's search box and grows downward from it, so there is
   * one search box on screen, not a box in the header plus a second one inside a dialog. Everything inside (modes, results, keyboard) is unchanged.
   * Off (the default) = the centred dialog exactly as before.
   */
  @Input() anchored = false;
  private readonly plainDialogStyle: Record<string, string> = { width: '620px' };
  private anchoredDialogStyle: Record<string, string> = { width: '620px' };

  get dialogStyle(): Record<string, string> {
    return this.anchored ? this.anchoredDialogStyle : this.plainDialogStyle;
  }

  /** Where the dialog's own input row must sit so that it lands exactly on the header's search box. */
  private computeAnchor(): void {
    const pill = this.anchored ? (document.querySelector('.tms-hdr-searchpill') as HTMLElement | null) : null;
    if (!pill) {
      this.anchoredDialogStyle = { width: '620px' };
      return;
    }
    const r = pill.getBoundingClientRect();
    const width = Math.min(640, window.innerWidth - 24);
    const rowCenter = 35; // the input row is about 70px tall: its middle sits 35px under the dialog's top edge
    const top = Math.max(6, Math.round(r.top + r.height / 2 - rowCenter));
    const dx = Math.round(r.left + r.width / 2 - window.innerWidth / 2);
    const maxDx = Math.max(0, Math.floor((window.innerWidth - width) / 2) - 8);
    this.anchoredDialogStyle = {
      width: width + 'px',
      margin: top + 'px 0 0 0',
      position: 'relative',
      left: Math.max(-maxDx, Math.min(maxDx, dx)) + 'px',
    };
  }

  open(): void {
    this.computeAnchor();
    this.visible = true;
    this.mode = 'auto';
    this.queryText = '';
    this.autoMathResult = null;
    this.calculatorPrefill = null;
    // Deferred one tick: p-dialog's own content may not be instantiated (and these ViewChild
    // refs not yet resolved) in the same synchronous pass that flips `visible` true — this fires
    // each child's own open() (candidate-list loading, recent-search load; their dialog/visible
    // flag itself is irrelevant while embedded, see each component's `embedded` field comment)
    // only once Angular has actually had a chance to create them.
    setTimeout(() => {
      this.quickSearchRef?.open();
      this.globalSearchRef?.open();
    });
  }

  close(): void {
    this.visible = false;
    this.quickSearchRef?.close();
    this.globalSearchRef?.close();
    this.queryText = '';
    this.autoMathResult = null;
    this.calculatorPrefill = null;
  }

  setMode(mode: SearchMode): void {
    this.mode = mode;
    this.syncActiveChild();
  }

  onQueryTextChange(): void {
    const trimmed = this.queryText.trim();
    this.autoMathResult = (this.mode === 'auto' && isMathExpression(trimmed)) ? evaluateMathExpression(trimmed) : null;
    this.syncActiveChild();
  }

  private syncActiveChild(): void {
    const em = this.effectiveMode;
    if (em === 'records') this.globalSearchRef?.setQuery(this.queryText);
    else if (em === 'stock') this.quickSearchRef?.setQuery(this.queryText);
  }

  openCalculatorFromMath(): void {
    if (this.autoMathResult == null) return;
    this.calculatorPrefill = this.autoMathResult;
    this.mode = 'calculator';
  }

  /** Bridges QuickSearchComponent's per-row "quick calculator" button (Stock tab) into this
   *  shell's own Calculator mode — that button used to silently do nothing when embedded here,
   *  since QuickSearchComponent's own calculator dialog is suppressed in embedded mode and
   *  nothing picked up the request (real incident, 2026-09-06). Only startValue is carried
   *  through, matching openCalculatorFromMath()'s own prefill-only convention for this shell. */
  onQuickSearchCalculatorRequested(payload: { startValue: number | null }): void {
    this.calculatorPrefill = payload.startValue;
    this.mode = 'calculator';
  }

  onCalculatorClose(): void {
    // The calculator has no "back to search" affordance of its own (it only knows how to close
    // entirely) — Escape/close button here means "leave Calculator mode", not "close the whole
    // dialog", so land back on Auto rather than closing everything.
    this.mode = 'auto';
    this.calculatorPrefill = null;
  }

  onInputKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      return;
    }
    if (this.mode === 'auto' && this.autoMathResult !== null && event.key === 'Enter') {
      event.preventDefault();
      this.openCalculatorFromMath();
      return;
    }
    const em = this.effectiveMode;
    if (em === 'records') this.globalSearchRef?.onInputKeydown(event);
    else if (em === 'stock') this.quickSearchRef?.onInputKeydown(event);
  }

  openAllShortcuts(): void {
    this.close();
    this.keyboardShortcutsService.requestOpen();
  }
}
